import React, { useState } from 'react';
import { Filter, MapPin, Battery, Clock, Shield, Share2 } from 'lucide-react';
import RedesignShell from './RedesignShell';
import { Button, Card, Pill, Dot, SectionLabel } from '../ui';

const DEMO_KPIS = [
  { label: 'Active', value: 58, tone: 'ok', delta: '+3' },
  { label: 'Idle', value: 14, tone: 'warn', delta: '−1' },
  { label: 'Offline', value: 39, tone: 'bad', delta: '+2' },
];

const DEMO_DEVICES = [
  { id: 'GA07AM7569', label: 'Civil_Maintenance_Shamim', ago: '6m ago', status: 'stopped', battery: '82%' },
  { id: 'GA07AM7570', label: 'Narayan_Saha', ago: '6m ago', status: 'stopped', battery: '64%' },
  { id: 'GA07AM7578', label: 'Field_Ops_Ananya', ago: '12m ago', status: 'stopped', battery: '47%' },
  { id: 'GA07AM7576', label: 'Rohit_Usapkar_Ops', ago: '86d ago', status: 'inactive', battery: 'N/A' },
];

const FILTERS = ['All', 'Active', 'Idle', 'Offline'];

export function RedesignPreview({ onExit }) {
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeNav, setActiveNav] = useState('fleet');

  return (
    <RedesignShell
      activeNav={activeNav}
      onNavClick={setActiveNav}
      onLogout={onExit}
      userName="Ravi"
      headerTitle="Fleet"
      headerSubtitle="203 devices · Goa region"
      userTimestamp="24·04·26 · 11:05 AM"
      unreadAlerts={12}
      onRefresh={() => {}}
      onBell={() => {}}
      onSearch={() => {}}
    >
      <div className="flex h-full min-h-0">
        {/* FleetPanel column */}
        <aside className="w-[340px] shrink-0 bg-surface border-r border-line flex flex-col min-h-0">
          {/* KPI strip */}
          <div className="grid grid-cols-3 gap-2 p-3">
            {DEMO_KPIS.map((k) => (
              <Card key={k.label} className="p-3 flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Dot color={`var(--${k.tone})`} />
                  <span className="text-[11px] font-medium text-ink-3 uppercase tracking-[0.05em]">
                    {k.label}
                  </span>
                </div>
                <div className="font-mono text-[22px] font-semibold text-ink leading-none">
                  {k.value}
                </div>
                <div className="text-[11px] text-ink-3 font-mono">{k.delta}</div>
              </Card>
            ))}
          </div>

          {/* Search input (decorative in Phase 1) */}
          <div className="px-3 pb-2">
            <input
              type="text"
              placeholder="Search devices…"
              className="w-full h-[34px] px-3 rounded-lg bg-bg border border-line text-[13px] text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-accent-ring"
            />
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1.5 px-3 pb-2">
            {FILTERS.map((f) => {
              const isActive = activeFilter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setActiveFilter(f)}
                  className={
                    'h-7 px-2.5 rounded-full text-[11.5px] font-medium border transition-colors ' +
                    (isActive
                      ? 'bg-ink text-surface border-ink'
                      : 'bg-surface text-ink-2 border-line hover:bg-bg')
                  }
                >
                  {f}
                </button>
              );
            })}
            <div className="flex-1" />
            <button
              type="button"
              className="w-7 h-7 rounded-full border border-line bg-surface text-ink-2 flex items-center justify-center hover:bg-bg"
              aria-label="More filters"
            >
              <Filter size={13} strokeWidth={1.7} />
            </button>
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between px-3 py-2 border-y border-line-2 bg-surface-2">
            <label className="flex items-center gap-2 text-[12px] text-ink-2">
              <input type="checkbox" defaultChecked className="accent-accent" />
              Device names
            </label>
            <label className="flex items-center gap-2 text-[12px] text-ink-2">
              <input type="checkbox" className="accent-accent" />
              Geofences
            </label>
          </div>

          {/* Fleet list */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            <SectionLabel right={<button className="text-[11px] text-accent hover:underline">Expand all</button>}>
              Fleet devices
            </SectionLabel>
            {DEMO_DEVICES.map((d) => (
              <Card key={d.id} className="p-3 flex items-center gap-3 hover:border-accent-ring transition-colors cursor-pointer">
                <div className="w-8 h-8 rounded-lg bg-accent-soft text-accent flex items-center justify-center">
                  <MapPin size={14} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11.5px] text-ink-3 leading-tight">{d.id}</div>
                  <div className="text-[12.5px] font-medium text-ink truncate">{d.label}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Pill tone={d.status === 'inactive' ? 'bad' : 'warn'}>
                    <Dot color={d.status === 'inactive' ? 'var(--bad)' : 'var(--warn)'} />
                    {d.status}
                  </Pill>
                  <span className="text-[10.5px] text-ink-3 font-mono">{d.ago}</span>
                </div>
              </Card>
            ))}
          </div>

          {/* Sticky footer */}
          <div className="p-3 border-t border-line bg-surface">
            <Button variant="dark" block icon={Shield}>
              Admin Panel
            </Button>
          </div>
        </aside>

        {/* MapStage placeholder */}
        <section className="flex-1 relative bg-bg overflow-hidden">
          {/* fake map */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 30% 40%, rgba(43,87,255,0.08) 0, transparent 60%), radial-gradient(circle at 70% 60%, rgba(116,87,216,0.06) 0, transparent 60%), linear-gradient(180deg, #eef2f7 0%, #e4ebf3 100%)',
            }}
          />
          {/* top-left toolbar */}
          <div className="absolute top-4 left-4 flex items-center gap-1 bg-surface border border-line rounded-lg shadow-card p-1">
            {['Layers', 'Live', 'Trails', 'Geofence'].map((t) => (
              <button
                key={t}
                className="h-8 px-3 rounded-md text-[12px] font-medium text-ink-2 hover:bg-bg"
              >
                {t}
              </button>
            ))}
          </div>
          {/* top-right live chip */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-surface border border-line rounded-full shadow-card px-3 py-1.5">
            <Dot color="var(--ok)" />
            <span className="text-[11.5px] font-medium text-ink-2">Live</span>
            <span className="font-mono text-[10.5px] text-ink-3">· last sync 4s ago</span>
          </div>
          {/* bottom-right zoom */}
          <div className="absolute bottom-4 right-4 flex flex-col bg-surface border border-line rounded-lg shadow-card overflow-hidden">
            <button className="w-9 h-9 flex items-center justify-center text-ink-2 hover:bg-bg border-b border-line">
              +
            </button>
            <button className="w-9 h-9 flex items-center justify-center text-ink-2 hover:bg-bg">
              −
            </button>
          </div>
          {/* bottom-left legend */}
          <div className="absolute bottom-4 left-4 flex items-center gap-3 bg-surface border border-line rounded-lg shadow-card px-3 py-2">
            {[
              { label: 'Active', color: 'var(--ok)' },
              { label: 'Idle', color: 'var(--warn)' },
              { label: 'Offline', color: 'var(--bad)' },
              { label: 'Cluster', color: 'var(--purple)' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <Dot color={l.color} />
                <span className="text-[11px] text-ink-2">{l.label}</span>
              </div>
            ))}
          </div>

          {/* Exit-preview badge */}
          <button
            type="button"
            onClick={onExit}
            className="absolute top-20 right-4 flex items-center gap-2 bg-ink text-surface rounded-full px-3.5 py-1.5 text-[11.5px] font-medium shadow-drawer hover:bg-ink-2 transition-colors"
          >
            <Share2 size={12} strokeWidth={1.7} />
            Exit redesign preview
          </button>

          {/* Center marker cluster */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-purple text-surface flex items-center justify-center font-semibold shadow-drawer"
                 style={{ boxShadow: '0 0 0 5px rgba(116,87,216,0.18)' }}>
              23
            </div>
          </div>
        </section>
      </div>
    </RedesignShell>
  );
}

export default RedesignPreview;
